import { onMounted, onUnmounted, Ref, watch } from "vue";

import { isNativePlatform } from "../capacitor/platform";

import { createPollTimer, shouldSuppressBackgroundPolling } from "./backgroundPolling";

/**
 * Polling intervals in milliseconds
 */
export const AGGRESSIVE_POLL_INTERVAL = 1000; // 1 second
export const UNSTABLE_POLL_INTERVAL = 15000; // 15 seconds
export const STABLE_POLL_INTERVAL = 30000; // 30 seconds
export const AGGRESSIVE_PHASE_DURATION = 30000; // 30 seconds
export const EVENT_DEBOUNCE_MS = 500; // Debounce for event-triggered fetches
// A Published grant waits for the device's next allowlist pull, on the device's
// own schedule, so the poll backs off from 5 seconds to the stable cadence.
export const PUBLISHED_POLL_INTERVALS = [5000, 10000, 20000, STABLE_POLL_INTERVAL];
// checkPublished runs every second for this long, then every PUBLISHED_CHECK_INTERVAL.
export const PUBLISHED_CHECK_FAST_MS = 30000;
export const PUBLISHED_CHECK_INTERVAL = 5000;
// Interval used by the native Android foreground service when the app is
// backgrounded. Kept longer than STABLE_POLL_INTERVAL because the FGS only
// needs to catch silent public-IP rotations (CGNAT, ISP DHCP, VPN egress)
// that NetworkCallback does not surface; all other network changes trigger
// an immediate refresh via the callback.
export const BACKGROUND_POLL_INTERVAL = 60000; // 60 seconds

/**
 * Extended Navigator interface to include Network Information API
 * @see https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation
 */
interface NetworkInformation extends EventTarget {
  readonly downlink?: number;
  readonly effectiveType?: "slow-2g" | "2g" | "3g" | "4g";
  readonly rtt?: number;
  readonly saveData?: boolean;
  readonly type?: string;
  onchange?: EventListener;
}

interface NavigatorWithConnection extends Navigator {
  readonly connection?: NetworkInformation;
  readonly mozConnection?: NetworkInformation;
  readonly webkitConnection?: NetworkInformation;
}

/**
 * Gets the Network Information API connection object if available
 * Handles vendor prefixes for cross-browser support
 */
export function getNetworkConnection(): NetworkInformation | undefined {
  const nav = navigator as NavigatorWithConnection;
  return nav.connection || nav.mozConnection || nav.webkitConnection;
}

interface UseStatusPollingOptions {
  /**
   * Function to fetch the current status
   */
  // What it resolves with is the caller's: the poll only waits for it.
  fetchStatus: () => Promise<unknown>;

  /**
   * Reactive reference indicating if there are unstable ACLs
   */
  hasUnstableAcls: Ref<boolean>;

  /** A grant is on an allowlist, waiting for the device to pull it. */
  hasPublishedAcls?: Ref<boolean>;

  /** Cheap check while a grant is Published. Resolving true fetches the whole status. */
  checkPublished?: () => Promise<boolean>;

  /**
   * Whether to automatically start polling on mount (default: true)
   */
  autoStart?: boolean;

  /**
   * Optional initial burst phase: poll every `intervalMs` for the first
   * `durationMs`, then fall back to the 1s aggressive cadence. Bypasses
   * the event debouncer so every tick fires.
   */
  burstPoll?: {
    intervalMs: number;
    durationMs: number;
    // Ends the burst once nothing is unstable.
    untilStable?: boolean;
  };
}

interface UseStatusPollingReturn {
  /**
   * Triggers aggressive polling for 30 seconds.
   * Call this after grant/revoke actions.
   */
  triggerAggressivePolling: () => void;

  /**
   * Manually starts the polling intervals.
   * Only needed if autoStart is false.
   */
  start: () => void;

  /**
   * Stops all polling intervals and removes event listeners.
   */
  stop: () => void;
}

/**
 * Polls fast while grants are unstable and slowly once they settle. The timer runs
 * in a Web Worker because browsers throttle background-tab timers, so a hidden
 * tab still notices an address change, at the stable cadence and no faster.
 */
export function useStatusPolling(options: UseStatusPollingOptions): UseStatusPollingReturn {
  const { fetchStatus, hasUnstableAcls, hasPublishedAcls, checkPublished, autoStart = true, burstPoll } = options;

  let pollTimer: ReturnType<typeof createPollTimer> | null = null;
  let pollCount = 0;
  let aggressivePollUntil = 0;
  let burstPollUntil = 0;
  let burstTransitionId: ReturnType<typeof setTimeout> | null = null;
  let isStarted = false;
  let lastEventFetchTime = 0;
  let publishedStep = 0;
  let publishedPollAt = 0;
  let publishedSince = 0;
  let lastPublishedCheck = 0;
  let checkingPublished = false;

  function resetPublishedBackoff(): void {
    const now = Date.now();
    publishedStep = 0;
    publishedPollAt = now + PUBLISHED_POLL_INTERVALS[0];
    publishedSince = now;
    lastPublishedCheck = 0;
  }

  // One at a time, so a slow answer is not overtaken by the next tick's.
  async function publishedMoved(now: number): Promise<boolean> {
    if (!checkPublished || checkingPublished || shouldSuppressBackgroundPolling()) {
      return false;
    }
    const fast = now - publishedSince < PUBLISHED_CHECK_FAST_MS;
    if (!fast && now - lastPublishedCheck < PUBLISHED_CHECK_INTERVAL) {
      return false;
    }
    lastPublishedCheck = now;
    checkingPublished = true;
    try {
      return await checkPublished();
    } finally {
      checkingPublished = false;
    }
  }

  /**
   * Triggers aggressive polling for 30 seconds
   */
  function triggerAggressivePolling(): void {
    aggressivePollUntil = Date.now() + AGGRESSIVE_PHASE_DURATION;
    resetPublishedBackoff();
  }

  /**
   * Debounced fetch to prevent multiple rapid API calls.
   * Used by all fetch triggers (events and interval polling) to prevent
   * redundant calls when multiple triggers fire close together.
   */
  async function debouncedFetch(): Promise<void> {
    if (shouldSuppressBackgroundPolling()) {
      return;
    }
    const now = Date.now();
    if (now - lastEventFetchTime < EVENT_DEBOUNCE_MS) {
      return;
    }
    lastEventFetchTime = now;
    await fetchStatus();
  }

  async function burstFetch(): Promise<void> {
    if (shouldSuppressBackgroundPolling()) {
      return;
    }
    lastEventFetchTime = Date.now();
    await fetchStatus();
  }

  /**
   * Handles the window focus event by fetching status
   */
  async function handleWindowFocus(): Promise<void> {
    await debouncedFetch();
  }

  /**
   * Handles the browser coming back online
   * Fetches status - if there are unstable ACLs, the watcher will trigger aggressive polling
   */
  async function handleOnline(): Promise<void> {
    await debouncedFetch();
  }

  /**
   * Handles network connection changes (Network Information API).
   * Triggers aggressive polling because a network change often precedes
   * an IP change by a few seconds as the OS configures the new interface.
   */
  async function handleNetworkChange(): Promise<void> {
    triggerAggressivePolling();
    await debouncedFetch();
  }

  /**
   * Handles the tab becoming visible again.
   * Triggers aggressive polling to quickly detect any IP changes
   * that occurred while the tab was in the background.
   */
  async function handleVisibilityChange(): Promise<void> {
    if (document.visibilityState === "visible") {
      triggerAggressivePolling();
      await debouncedFetch();
    }
  }

  /**
   * Called on each tick from the poll timer (Web Worker or fallback).
   * Determines whether to fetch based on current polling phase.
   */
  async function handleTick(): Promise<void> {
    pollCount++;

    const now = Date.now();

    const inBurstPhase = burstPollUntil > 0 && now <= burstPollUntil;
    const inAggressivePhase = now < aggressivePollUntil;

    try {
      if (document.visibilityState === "hidden") {
        // Only often enough to notice an address change.
        if (now - lastEventFetchTime >= STABLE_POLL_INTERVAL) {
          await debouncedFetch();
        }
        return;
      }
      if (inBurstPhase && (!burstPoll?.untilStable || hasUnstableAcls.value)) {
        await burstFetch();
        return;
      }
      if (hasUnstableAcls.value) {
        if (inAggressivePhase) {
          // Aggressive phase: poll every 1 second for 30 seconds
          await debouncedFetch();
        } else if (pollCount % (UNSTABLE_POLL_INTERVAL / AGGRESSIVE_POLL_INTERVAL) === 0) {
          // Non-aggressive but unstable: poll every 15 seconds
          await debouncedFetch();
        }
      } else if (hasPublishedAcls?.value) {
        if (await publishedMoved(now)) {
          await debouncedFetch();
        } else if (now >= publishedPollAt) {
          publishedStep = Math.min(publishedStep + 1, PUBLISHED_POLL_INTERVALS.length - 1);
          publishedPollAt = now + PUBLISHED_POLL_INTERVALS[publishedStep];
          await debouncedFetch();
        }
      } else if (pollCount % (STABLE_POLL_INTERVAL / AGGRESSIVE_POLL_INTERVAL) === 0) {
        // Stable: poll every 30 seconds
        await debouncedFetch();
      }
    } catch {
      // Errors during status polling are silently ignored to prevent
      // breaking the polling loop. The caller should handle errors
      // in their fetchStatus implementation if needed.
    }
  }

  /**
   * Starts the polling interval and event listeners
   */
  function start(): void {
    if (isStarted) {
      return;
    }
    isStarted = true;

    // Start aggressive polling if there are unstable ACLs on start
    if (hasUnstableAcls.value) {
      triggerAggressivePolling();
    }
    resetPublishedBackoff();

    pollCount = 0;
    pollTimer = createPollTimer(handleTick);
    if (burstPoll) {
      burstPollUntil = Date.now() + burstPoll.durationMs;
      pollTimer.start(burstPoll.intervalMs);
      burstTransitionId = setTimeout(() => {
        if (pollTimer) {
          pollTimer.start(AGGRESSIVE_POLL_INTERVAL);
        }
        burstTransitionId = null;
      }, burstPoll.durationMs);
    } else {
      pollTimer.start(AGGRESSIVE_POLL_INTERVAL);
    }

    // Poll when tab becomes visible (more reliable than focus on mobile)
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Poll on window focus
    window.addEventListener("focus", handleWindowFocus);

    // Poll when browser comes back online
    window.addEventListener("online", handleOnline);

    // Poll on network connection changes (Network Information API, Chromium only)
    const connection = getNetworkConnection();
    if (connection) {
      connection.addEventListener("change", handleNetworkChange);
    }

    // Layer 2 + 3: Capacitor native listeners (Android/iOS)
    if (isNativePlatform()) {
      import("../capacitor/native-listeners").then(({ registerNativeListeners }) => {
        registerNativeListeners({
          onAppResumed: () => {
            triggerAggressivePolling();
            debouncedFetch();
          },
          onNetworkChanged: () => {
            triggerAggressivePolling();
            debouncedFetch();
          },
        });
      });

      import("../capacitor/ip-change-plugin")
        .then(({ default: IpChange }) =>
          IpChange.addListener("ipChanged", () => {
            triggerAggressivePolling();
            debouncedFetch();
          }),
        )
        .catch(() => {
          // Native plugin may be unavailable (e.g. under test); fall back to
          // regular polling without the IP-change optimisation.
        });
    }
  }

  /**
   * Stops polling and removes event listeners
   */
  function stop(): void {
    if (pollTimer) {
      pollTimer.stop();
      pollTimer = null;
    }
    if (burstTransitionId !== null) {
      clearTimeout(burstTransitionId);
      burstTransitionId = null;
    }
    burstPollUntil = 0;
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("focus", handleWindowFocus);
    window.removeEventListener("online", handleOnline);

    const connection = getNetworkConnection();
    if (connection) {
      connection.removeEventListener("change", handleNetworkChange);
    }

    if (isNativePlatform()) {
      import("../capacitor/native-listeners").then(({ removeNativeListeners }) => {
        removeNativeListeners();
      });
      import("../capacitor/ip-change-plugin")
        .then(({ default: IpChange }) => IpChange.removeAllListeners())
        .catch(() => {
          // Native plugin may be unavailable; nothing to clean up.
        });
    }

    isStarted = false;
  }

  // Watch for changes to hasUnstableAcls to trigger aggressive polling
  watch(hasUnstableAcls, (isUnstable, wasUnstable) => {
    if (isUnstable && !wasUnstable) {
      // ACLs became unstable, trigger aggressive polling
      triggerAggressivePolling();
    }
  });

  if (hasPublishedAcls) {
    watch(hasPublishedAcls, (isPublished, wasPublished) => {
      if (isPublished && !wasPublished) {
        resetPublishedBackoff();
      }
    });
  }

  if (autoStart) {
    onMounted(() => {
      start();
    });

    onUnmounted(() => {
      stop();
    });
  }

  return {
    triggerAggressivePolling,
    start,
    stop,
  };
}
